import React, { useState, useEffect } from 'react';
import { KeyRound, ShieldCheck, History, Lock } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { api } from '../../lib/api';
import { requestPin } from '../../components/PinModal';
import { RecaptchaV2, executeV3 } from '../../components/RecaptchaWidget';
import { toast } from 'sonner';

export default function AdminSeguridad() {
  const [log, setLog] = useState(null);
  const [rc, setRc] = useState(null);
  const [rcForm, setRcForm] = useState({ secretKey: '' });
  const [testing, setTesting] = useState(false);
  const [testWidget, setTestWidget] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', repeat: '' });
  const [pinForm, setPinForm] = useState({ password: '', newPin: '' });

  useEffect(() => {
    api.getAccessLog().then(setLog).catch(() => setLog([]));
    api.getRecaptchaAdmin().then(setRc).catch(() => {});
  }, []);

  const saveRc = async (overrides = {}) => {
    try {
      const updated = await api.updateRecaptcha({
        enabled: rc.enabled, version: rc.version, siteKey: rc.siteKeyFromEnv ? '' : rc.siteKey,
        secretKey: rcForm.secretKey, ...overrides,
      });
      setRc(updated);
      setRcForm({ secretKey: '' });
      toast.success('Configuración reCAPTCHA guardada');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const runTest = async (token) => {
    setTesting(true);
    setTestWidget(false);
    try {
      const res = await api.testRecaptcha(rcForm.secretKey, token);
      res.ok ? toast.success(res.message) : toast.error(res.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo probar'); }
    finally { setTesting(false); }
  };

  const testKeys = async () => {
    if (!rc.siteKey) { toast.error('Añade primero la clave de sitio'); return; }
    if (rc.version === 'v3') {
      setTesting(true);
      try {
        const token = await executeV3(rc.siteKey, 'test');
        await runTest(token);
      } catch { toast.error('La clave de sitio v3 no es válida'); setTesting(false); }
    } else {
      setTestWidget(true);
      toast.info('Resuelve el captcha de prueba que ha aparecido para verificar las claves');
    }
  };

  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (pw.next !== pw.repeat) { toast.error('Las contraseñas nuevas no coinciden'); return; }
    const token = await requestPin('Cambiar la contraseña de acceso');
    if (!token) return;
    try {
      await api.changePassword(pw.current, pw.next, token);
      setPw({ current: '', next: '', repeat: '' });
      toast.success('Contraseña actualizada');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cambiar la contraseña'); }
  };

  const changePin = async () => {
    if (!/^\d{4}$/.test(pinForm.newPin)) { toast.error('El PIN debe tener exactamente 4 dígitos'); return; }
    try {
      await api.changePin(pinForm.password, pinForm.newPin);
      setPinForm({ password: '', newPin: '' });
      toast.success('PIN maestro actualizado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cambiar el PIN'); }
  };

  const inputCls = 'w-full border border-stone-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400';

  return (
    <div data-testid="admin-seguridad">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-black text-stone-800">Seguridad</h1>
      </div>
      <p className="text-sm text-stone-500 mb-6">Credenciales de acceso al Panel MATRIZ y registro de accesos.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        {/* Cambiar contraseña */}
        <div className="bg-white rounded-lg border border-stone-200 p-6" data-testid="security-password-panel">
          <h2 className="font-black text-stone-800 mb-1 flex items-center gap-2"><Lock className="w-4 h-4 text-stone-400" /> Contraseña de acceso</h2>
          <p className="text-xs text-stone-500 mb-4">Cámbiala periódicamente. Mínimo 8 caracteres.</p>
          <div className="space-y-3">
            <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder="Contraseña actual" className={inputCls} data-testid="security-current-password" />
            <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="Nueva contraseña" className={inputCls} data-testid="security-new-password" />
            <input type="password" value={pw.repeat} onChange={(e) => setPw({ ...pw, repeat: e.target.value })} placeholder="Repite la nueva contraseña" className={inputCls} data-testid="security-repeat-password" />
            <button onClick={changePassword} className="btn-natur px-5 py-2.5 rounded text-sm font-bold" data-testid="security-change-password-button">Cambiar contraseña</button>
          </div>
        </div>

        {/* Cambiar PIN */}
        <div className="bg-white rounded-lg border border-stone-200 p-6" data-testid="security-pin-panel">
          <h2 className="font-black text-stone-800 mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4 text-stone-400" /> PIN maestro</h2>
          <p className="text-xs text-stone-500 mb-4">4 dígitos. Autoriza acciones sensibles (eliminar, publicar legal, mantenimiento). Sesión segura de 10 minutos tras introducirlo.</p>
          <div className="space-y-3">
            <input type="password" value={pinForm.password} onChange={(e) => setPinForm({ ...pinForm, password: e.target.value })} placeholder="Tu contraseña (para confirmar)" className={inputCls} data-testid="security-pin-password" />
            <input type="password" inputMode="numeric" maxLength={4} value={pinForm.newPin} onChange={(e) => setPinForm({ ...pinForm, newPin: e.target.value.replace(/\D/g, '') })} placeholder="Nuevo PIN (4 dígitos)" className={`${inputCls} tracking-widest`} data-testid="security-new-pin" />
            <button onClick={changePin} className="btn-natur px-5 py-2.5 rounded text-sm font-bold" data-testid="security-change-pin-button">Cambiar PIN</button>
          </div>
        </div>

        {/* reCAPTCHA */}
        <div className="bg-white rounded-lg border border-stone-200 p-6" data-testid="security-recaptcha-panel">
          <h2 className="font-black text-stone-800 mb-1">reCAPTCHA en login y PIN</h2>
          <p className="text-xs text-stone-500 mb-4">Protege el acceso y el PIN maestro. Guarda tus claves (o déjalas en el .env), pruébalas y actívalo cuando estés seguro.</p>
          {rc ? (
            <div className="space-y-3">
              <div className={`flex items-center gap-3 rounded border px-3 py-2.5 ${rc.enabled ? 'bg-teal-50 border-teal-200' : 'bg-stone-50 border-stone-200'}`}>
                <Switch checked={rc.enabled} onCheckedChange={(v) => saveRc({ enabled: v })} data-testid="recaptcha-enabled-switch" />
                <span className="text-sm font-bold text-stone-700">{rc.enabled ? 'Activado' : 'Desactivado'}</span>
                <select value={rc.version} onChange={(e) => setRc({ ...rc, version: e.target.value })} className="ml-auto border border-stone-300 rounded px-2 py-1.5 text-xs bg-white" data-testid="recaptcha-version-select">
                  <option value="v2">v2 — casilla "No soy un robot"</option>
                  <option value="v3">v3 — invisible (puntuación)</option>
                </select>
              </div>
              <input value={rc.siteKey} onChange={(e) => setRc({ ...rc, siteKey: e.target.value, siteKeyFromEnv: false })} placeholder="Clave de sitio (site key)" className={inputCls} data-testid="recaptcha-sitekey-input" />
              <input type="password" value={rcForm.secretKey} onChange={(e) => setRcForm({ secretKey: e.target.value })} placeholder={rc.hasSecretKey ? 'Clave secreta (guardada ••••••)' : 'Clave secreta (secret key)'} className={inputCls} data-testid="recaptcha-secretkey-input" />
              <div className="flex gap-2">
                <button onClick={() => saveRc()} className="btn-natur px-4 py-2 rounded text-sm font-bold" data-testid="recaptcha-save-button">Guardar</button>
                <button onClick={testKeys} disabled={testing} className="border border-stone-300 hover:border-amber-400 transition-colors px-4 py-2 rounded text-sm font-bold text-stone-600 disabled:opacity-60" data-testid="recaptcha-test-button">{testing ? 'Probando…' : 'Probar claves'}</button>
              </div>
              {testWidget && <RecaptchaV2 key={rc.siteKey} siteKey={rc.siteKey} onToken={(t) => t && runTest(t)} />}
              {rc.hasSecretKey && !rc.secretKeyFromEnv && (
                <button onClick={() => saveRc({ clearSecret: true, enabled: false })} className="text-[11px] font-bold text-red-400 hover:text-red-600 transition-colors underline" data-testid="recaptcha-clear-secret">Borrar clave secreta guardada</button>
              )}
              <p className="text-[11px] text-stone-400">Claves gratis en google.com/recaptcha (elige el tipo que coincida con la versión). {rc.siteKeyFromEnv || rc.secretKeyFromEnv ? 'Actualmente usando claves del .env como respaldo.' : ''} Bloqueo adicional: 15 min tras 5 intentos fallidos.</p>
            </div>
          ) : <p className="text-sm text-stone-400">Cargando…</p>}
        </div>

        {/* Registro de accesos */}
        <div className="bg-white rounded-lg border border-stone-200 p-6" data-testid="security-access-log">
          <h2 className="font-black text-stone-800 mb-3 flex items-center gap-2"><History className="w-4 h-4 text-stone-400" /> Últimos accesos</h2>
          {log === null ? (
            <p className="text-sm text-stone-400">Cargando…</p>
          ) : log.length === 0 ? (
            <p className="text-sm text-stone-400">Sin registros todavía.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {log.map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${l.ok ? 'bg-teal-500' : 'bg-red-500'}`} />
                  <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${l.kind === 'pin' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-500'}`}>{l.kind === 'pin' ? 'PIN' : 'Login'}</span>
                  <span className="text-stone-600 flex-1">{new Date(l.at).toLocaleString('es-ES')}</span>
                  <span className="text-stone-400">{l.ip}</span>
                  <span className={`font-bold ${l.ok ? 'text-teal-600' : 'text-red-500'}`}>{l.ok ? 'Correcto' : 'Fallido'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
